import { useMemo } from "react";

import {
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { caseHintsApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import { TaskIdExtractor } from "src/cases/queries/casesTypes";
import {
  ActionTriggeredEventDataActionFormDataValue,
  CaseActionHintPatchResponse,
  CaseHintsResponse,
  CaseTaskHintPatchResponse,
  ModelsCaseDataApiModelsCaseActionHintPatchRequestDataTypeEnum,
  ModelsCaseDataApiModelsCaseTaskHintPatchRequestDataTypeEnum,
} from "src/clients/case-management";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const caseHintsKeys = {
  all: (workspaceId: string) => ["caseHints", workspaceId] as const,
  get: (workspaceId: string, caseId: string) =>
    [...caseHintsKeys.all(workspaceId), "hints", caseId] as const,
};

const useCaseHintsClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  return useMemo(
    () => (workspace?.base_url ? caseHintsApi(workspace.base_url) : undefined),
    [workspace?.base_url],
  );
};

const useHintsWorkspaceParams = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useCaseHintsClient();

  return { wsId, apiClient };
};

const HINTS_STALE_TIME = 5 * 60 * 1000; // 5 minutes

export const useCaseHints = <TData = CaseHintsResponse>(
  caseId?: string,
  options?: Omit<
    UseQueryOptions<CaseHintsResponse, AxiosError, TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const { wsId, apiClient } = useHintsWorkspaceParams();

  return useQuery<CaseHintsResponse, AxiosError, TData>({
    queryKey: caseHintsKeys.get(wsId, caseId!),
    queryFn: async () => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is missing");
      }

      const response = await apiClient.getCaseHintsApiV1CasesCaseIdHintsGet({
        caseId,
      });
      return response.data;
    },
    staleTime: HINTS_STALE_TIME,
    enabled:
      !!apiClient &&
      !!caseId &&
      isFeatureFlagEnabled(FEATURE_FLAGS.caseCopilot),
    ...options,
  });
};

export const selectTaskKeysWithHints = (
  data: CaseHintsResponse,
): Set<string> => {
  const hints = data.data?.attributes?.task_form_hints;
  if (!hints) return new Set<string>();
  return new Set(
    Object.values(hints).flatMap((statusHints) =>
      Object.entries(statusHints)
        .filter(([, fieldHints]) => Object.keys(fieldHints).length > 0)
        .map(([taskKey]) => taskKey),
    ),
  );
};

export const useTasksWithHints = (caseId?: string) => {
  const { data } = useCaseHints(caseId, {
    select: selectTaskKeysWithHints,
  });

  return data ?? new Set<string>();
};

export const selectTaskFieldHints = (
  data: CaseHintsResponse,
  taskId: string,
): Record<string, unknown> | undefined => {
  const statusKey = TaskIdExtractor.getStatusKey(taskId);
  const taskKey = TaskIdExtractor.getTaskKey(taskId);
  if (!statusKey || !taskKey) return undefined;

  const statusHints = data.data?.attributes?.task_form_hints?.[statusKey];
  if (!statusHints) return undefined;

  const fieldHints = statusHints[taskKey];
  if (!fieldHints || Object.keys(fieldHints).length === 0) return undefined;

  return fieldHints as Record<string, unknown>;
};

export const useTaskFieldHints = (caseId?: string, taskId?: string) => {
  return useCaseHints(caseId, {
    select: (data) => (taskId ? selectTaskFieldHints(data, taskId) : undefined),
  });
};

type PatchTaskHintParams = {
  caseId: string;
  taskKey: string;
  hintId: string;
  attributes: Record<string, ActionTriggeredEventDataActionFormDataValue>;
};

export const usePatchTaskHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTaskHintPatchResponse,
    AxiosError,
    PatchTaskHintParams
  >({
    mutationFn: async ({ caseId, taskKey, hintId, attributes }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      const response =
        await apiClient.patchCaseTaskHintApiV1CasesCaseIdTasksTaskKeyHintPatch({
          caseId,
          taskKey,
          caseTaskHintPatchRequest: {
            data: {
              type: ModelsCaseDataApiModelsCaseTaskHintPatchRequestDataTypeEnum.CASE_TASK_HINT,
              id: hintId,
              attributes,
            },
          },
        });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, variables.caseId),
      });
    },
  });
};

type PatchActionHintParams = {
  caseId: string;
  actionKey: string;
  hintId: string;
  attributes: Record<string, ActionTriggeredEventDataActionFormDataValue>;
};

export const usePatchActionHint = () => {
  const { wsId, apiClient } = useHintsWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseActionHintPatchResponse,
    AxiosError,
    PatchActionHintParams
  >({
    mutationFn: async ({ caseId, actionKey, hintId, attributes }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      const response =
        await apiClient.patchCaseActionHintApiV1CasesCaseIdActionsActionKeyHintPatch(
          {
            caseId,
            actionKey,
            caseActionHintPatchRequest: {
              data: {
                type: ModelsCaseDataApiModelsCaseActionHintPatchRequestDataTypeEnum.CASE_ACTION_HINT,
                id: hintId,
                attributes,
              },
            },
          },
        );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, variables.caseId),
      });
    },
  });
};
